import {
  DIFFICULTIES,
  type DifficultyDefinition,
  type DifficultyId,
} from "./manifest";

export const PAIRS_TO_CLEAR = 10;

export type FeedbackKind = "correct" | "wrong";
export type GamePhase = "playing" | "feedback" | "finished";

export interface GameFeedback {
  readonly kind: FeedbackKind;
  readonly indices: readonly [number, number];
}

export interface GameState {
  readonly difficultyId: DifficultyId;
  readonly cards: readonly number[];
  readonly target: number;
  readonly selectedIndex: number | null;
  readonly correctCount: number;
  readonly mistakeCount: number;
  readonly phase: GamePhase;
  readonly feedback: GameFeedback | null;
}

function randomInteger(min: number, max: number, random: () => number): number {
  return Math.floor(random() * (max - min + 1)) + min;
}

function pick<T>(items: readonly T[], random: () => number): T {
  const item = items[randomInteger(0, items.length - 1, random)];
  if (item === undefined) throw new Error("Cannot pick from an empty list");
  return item;
}

function getDifficulty(difficultyId: DifficultyId): DifficultyDefinition {
  return DIFFICULTIES[difficultyId];
}

function getNextTarget(
  difficulty: DifficultyDefinition,
  previousTarget: number,
  random: () => number,
): number {
  const candidates: number[] = [];
  for (let target = difficulty.minTarget; target <= difficulty.maxTarget; target += 1) {
    if (target !== previousTarget) candidates.push(target);
  }
  return pick(candidates, random);
}

function createPairForTarget(target: number, random: () => number): [number, number] {
  const pairs: Array<[number, number]> = [];
  for (let first = 1; first <= 10; first += 1) {
    const second = target - first;
    if (second >= first && second <= 10) pairs.push([first, second]);
  }
  return pick(pairs, random);
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = randomInteger(0, index, random);
    const current = shuffled[index];
    const swap = shuffled[swapIndex];
    if (current === undefined || swap === undefined) throw new Error("Invalid shuffle index");
    shuffled[index] = swap;
    shuffled[swapIndex] = current;
  }
  return shuffled;
}

function createCardsForTarget(
  target: number,
  boardSize: DifficultyDefinition["boardSize"],
  random: () => number,
): number[] {
  const maxCardValue = Math.min(10, target - 1);
  const guaranteedPair = createPairForTarget(target, random);
  const cards = [...guaranteedPair];
  const usedValues = new Set(guaranteedPair);
  const unusedValues = shuffle(
    Array.from({ length: maxCardValue }, (_, index) => index + 1).filter(
      (value) => !usedValues.has(value),
    ),
    random,
  );

  while (cards.length < boardSize) {
    const value = unusedValues.pop();
    if (value === undefined) throw new Error("Not enough unique card values");
    cards.push(value);
  }

  return shuffle(cards, random);
}

export function findValidPairs(cards: readonly number[], target: number): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  for (let first = 0; first < cards.length; first += 1) {
    for (let second = first + 1; second < cards.length; second += 1) {
      if (cards[first]! + cards[second]! === target) pairs.push([first, second]);
    }
  }
  return pairs;
}

export function hasValidPair(cards: readonly number[], target: number): boolean {
  return findValidPairs(cards, target).length > 0;
}

export function createGame(difficultyId: DifficultyId, random: () => number = Math.random): GameState {
  const difficulty = getDifficulty(difficultyId);
  const target = randomInteger(difficulty.minTarget, difficulty.maxTarget, random);
  return {
    difficultyId,
    cards: createCardsForTarget(target, difficulty.boardSize, random),
    target,
    selectedIndex: null,
    correctCount: 0,
    mistakeCount: 0,
    phase: "playing",
    feedback: null,
  };
}

export function chooseCard(state: GameState, index: number): GameState {
  if (!Number.isInteger(index) || index < 0 || index >= state.cards.length) {
    throw new RangeError(`Card index out of range: ${index}`);
  }
  if (state.phase !== "playing") return state;

  if (state.selectedIndex === null) {
    return { ...state, selectedIndex: index, feedback: null };
  }
  if (state.selectedIndex === index) {
    return { ...state, selectedIndex: null, feedback: null };
  }

  const firstIndex = state.selectedIndex;
  const indices: readonly [number, number] = [firstIndex, index];
  const correct = state.cards[firstIndex]! + state.cards[index]! === state.target;

  if (!correct) {
    return {
      ...state,
      selectedIndex: null,
      mistakeCount: state.mistakeCount + 1,
      phase: "feedback",
      feedback: { kind: "wrong", indices },
    };
  }

  const correctCount = state.correctCount + 1;
  return {
    ...state,
    selectedIndex: null,
    correctCount,
    phase: correctCount >= PAIRS_TO_CLEAR ? "finished" : "feedback",
    feedback: { kind: "correct", indices },
  };
}

export function continueAfterFeedback(
  state: GameState,
  random: () => number = Math.random,
): GameState {
  if (state.phase !== "feedback" || state.feedback === null) return state;
  if (state.feedback.kind === "wrong") {
    return { ...state, phase: "playing", feedback: null };
  }

  const difficulty = getDifficulty(state.difficultyId);
  const target = getNextTarget(difficulty, state.target, random);
  return {
    ...state,
    cards: createCardsForTarget(target, difficulty.boardSize, random),
    target,
    phase: "playing",
    feedback: null,
  };
}
